import EventEmitter from 'eventemitter3';
import {
  fetchSignalingConfiguration,
  signalingAccept,
  signalingCandidate,
  signalingOffer,
} from 'providers/core/adviseSessions';
import {isIOS} from 'react-device-detect';
import store from 'store';
import {sleep} from 'utils';
import SnackbarUtil from 'utils/snackbar';
import 'webrtc-adapter';
import {getAdviseSession} from '../hooks/adviseSessions';

const _singleton = Symbol();
const _singletonEnforcer = Symbol();

function bufferizeCandidates(pc) {
  const candidatesQueue = [];
  pc.addEventListener('signalingstatechange', function () {
    if (this.signalingState === 'stable') {
      while (candidatesQueue?.length) {
        const entry = candidatesQueue.shift();
        pc.addIceCandidate(entry.candidate);
      }
    }
  });
  return function (candidate) {
    switch (pc.signalingState) {
      case 'closed':
        throw new Error('PeerConnection object is closed');
      case 'stable':
        if (pc.remoteDescription) {
          pc.addIceCandidate(candidate);
          break;
        }
      // eslint-disable-next-line no-fallthrough
      default:
        candidatesQueue.push({
          candidate,
        });
    }
  };
}

export const WEB_RTC_CALL_STATUS = {
  NEW: 'NEW',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  CLOSED: 'CLOSED',
};

export default class WebRTC extends EventEmitter {
  constructor(enforcer) {
    super();
    if (enforcer !== _singletonEnforcer) {
      throw new Error('Cannot construct singleton');
    }

    window.addEventListener('logout', () => {
      this.leave().catch((error) => {
        console.error('Unable to close WebRTC call during logout:', error);
      });
    });

    window.addEventListener('session-duplicated', () => {
      this.leave().catch((error) => {
        console.error(
          'Unable to close WebRTC call after duplicated session:',
          error,
        );
      });
    });
  }

  /**
   * Get WebRTC instance
   * @returns {WebRTC}
   */
  static get instance() {
    if (!this[_singleton]) {
      this[_singleton] = new WebRTC(_singletonEnforcer);
    }
    return this[_singleton];
  }

  get RTCPeerConnection() {
    return (
      window.RTCPeerConnection ||
      window.mozRTCPeerConnection ||
      window.webkitRTCPeerConnection
    );
  }

  get isIdle() {
    return !this._connection;
  }

  get adviseSessionId() {
    return this._adviseSessionId;
  }

  get signalingId() {
    return this._signalingId;
  }

  /**
   * @returns {Promise<RTCPeerConnection>}
   */
  get connection() {
    if (!this._connection) {
      const getConnection = async () => {
        const {
          data: {iceServers},
        } = await fetchSignalingConfiguration();

        const connection = new this.RTCPeerConnection({
          iceServers: iceServers.map((iceServer) => {
            const {url, ...rest} = iceServer;

            return {
              ...rest,
              urls: url,
            };
          }),
        });
        connection.__iceCandidates = [];
        connection.__addIceCandidate = bufferizeCandidates(connection);

        connection.onaddstream = (e) => {
          this.remoteStream = e.stream;
        };

        connection.onconnectionstatechange = this.onConnectionStateChange;

        connection.onicecandidate = this.onIceCandidate;

        return connection;
      };
      this._connection = getConnection();
    }

    return this._connection;
  }

  onConnectionStateChange = (event) => {
    switch (event.target.connectionState) {
      case 'new':
        this.clearDisconnectTimer();
        store.getActions().webRtcCall.updateStatus(WEB_RTC_CALL_STATUS.NEW);
        break;
      case 'closed':
      case 'failed':
        this.clearDisconnectTimer();
        store.getActions().webRtcCall.updateStatus(WEB_RTC_CALL_STATUS.CLOSED);
        break;
      case 'disconnected':
        this.disconnectTimer = setTimeout(async () => {
          if (event.target.connectionState === 'disconnected') {
            await this.leave();
          }
        }, 5000);
        break;
      case 'connecting':
        this.clearDisconnectTimer();
        store
          .getActions()
          .webRtcCall.updateStatus(WEB_RTC_CALL_STATUS.CONNECTING);
        break;
      case 'connected':
        this.clearDisconnectTimer();
        store
          .getActions()
          .webRtcCall.updateStatus(WEB_RTC_CALL_STATUS.CONNECTED);
        break;
      default:
        console.warn(
          `Unexpected iceConnectionState status : ${event.target.connectionState}`,
        );
    }
  };

  clearDisconnectTimer = () => {
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = undefined;
    }
  };

  onIceCandidate = async (event) => {
    if (event.candidate && event.candidate.candidate) {
      if (this._signalingId) {
        const data = {
          candidate: event.candidate.candidate,
        };

        if (event.candidate.sdpMid !== null) {
          data.sdpMid = event.candidate.sdpMid;
        }

        if (event.candidate.sdpMLineIndex !== null) {
          data.sdpMLineIndex = event.candidate.sdpMLineIndex;
        }

        signalingCandidate(this._adviseSessionId, this._signalingId, data);
      } else if (this._connection) {
        const connection = await this.connection;
        connection.__iceCandidates.push(event);
      }
    }
  };

  get localStream() {
    return this._localStream;
  }

  set localStream(localStream) {
    this._localStream = localStream;
    this.localStreamMuted = !!this.localStreamMuted;
    this.emit('localStream', localStream);
  }

  get localStreamMuted() {
    return !!this._localStreamMuted;
  }

  set localStreamMuted(muted) {
    this._localStreamMuted = muted;
    if (this.localStream) {
      const tracks = this.localStream.getAudioTracks();
      for (const track of tracks) {
        track.enabled = !muted;
      }
    }
  }

  get remoteStream() {
    return this._remoteStream;
  }

  set remoteStream(remoteStream) {
    this._remoteStream = remoteStream;
    this.emit('remoteStream', remoteStream);
  }

  getMedia = async (withVideo) => {
    const constraints = {
      audio: {
        noiseSuppression: true,
        echoCancellation: true,
      },
    };
    const videoConstraints = {
      frameRate: 15,
      facingMode: {ideal: 'user'},
    };

    if (!isIOS) {
      videoConstraints.width = 318;
      videoConstraints.height = 180;
    }

    if (withVideo) {
      constraints.video = videoConstraints;
    } else {
      constraints.video = false;
    }

    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (e) {
      const device = withVideo ? 'دوربین یا میکروفون' : 'میکروفون';
      SnackbarUtil.error(
        `خطا در ارتباط با  ${device} ، لطفا مجوز دسترسی به ${device} را اعطا کنید از قسمت تنظیمات مرورگر به بخش مجوز ها رفته و اجازه دسترسی به برنامه پزشکت رو فعال نمایید`,
        {persist: true},
      );
      this.leave();
      throw e;
    }
  };

  release = async () => {
    this._localStreamMuted = undefined;
    this._signalingId = undefined;
    this._adviseSessionId = undefined;
    this._offer = undefined;
    this.clearDisconnectTimer();
    if (this._connectionTimer) {
      clearTimeout(this._connectionTimer);
      this._connectionTimer = undefined;
    }

    if (this.endTimer) {
      clearTimeout(this.endTimer);
      this.endTimer = undefined;
    }

    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = undefined;
    }
    if (this.remoteStream) {
      this.remoteStream.getTracks().forEach((track) => track.stop());
      this.remoteStream = undefined;
    }
    if (this._connection) {
      const connection = await this.connection;
      connection.close();
      this._connection = undefined;
    }
    store.getActions().webRtcCall.updateStatus(WEB_RTC_CALL_STATUS.NEW);
  };

  leave = async () => {
    store.getActions().webRtcCall.updateStatus(WEB_RTC_CALL_STATUS.CLOSED);
    await sleep(0);
    return this.release();
  };

  createOffer = async (withVideo, adviseSessionId, signalingId) => {
    await this.release();
    this._connectionTimer = setTimeout(async () => {
      if (this._connection) {
        const connection = await this.connection;
        if (connection.connectionState !== 'connected') {
          await this.leave();
        }
      }
    }, 30 * 1000);
    this._adviseSessionId = adviseSessionId;
    this.localStream = await this.getMedia(withVideo);
    const connection = await this.connection;
    connection.addStream(this.localStream);

    this._offer = await connection.createOffer();

    if (signalingId) {
      this._signalingId = signalingId;
      await signalingAccept(adviseSessionId, signalingId, {
        sdpOffer: this._offer.sdp,
      });
    } else {
      const {
        data: {signalingId},
      } = await signalingOffer(adviseSessionId, {
        sdpOffer: this._offer.sdp,
      });
      this._signalingId = signalingId;
      connection.__iceCandidates.forEach(this.onIceCandidate);
    }

    this.setEndTimer(adviseSessionId);
  };

  setEndTimer = async (adviseSessionId) => {
    try {
      if (this.endTimer) {
        return;
      }
      const session = await getAdviseSession(adviseSessionId);
      if (!session) {
        return;
      }
      const endTime = Math.floor(
        session.startAt + session.duration * 60 - new Date().getTime() / 1000,
      );
      if (endTime > 0) {
        this.endTimer = setTimeout(async () => {
          const session1 = await getAdviseSession(adviseSessionId);
          const tt = Math.floor(
            session1.startAt +
              session1.duration * 60 -
              new Date().getTime() / 1000,
          );
          if (tt <= 0) {
            this.leave();
          } else {
            clearTimeout(this.endTimer);
            this.endTimer = setTimeout(() => {
              this.leave();
            }, (tt + 60) * 1000);
          }
        }, (endTime + 60) * 1000);
      }
    } catch (e) {
      console.log(e);
    }
  };

  handleAnswer = async (sdpAnswer) => {
    if (this._connection) {
      const connection = await this.connection;
      connection.setLocalDescription(this._offer);
      connection.setRemoteDescription(
        new RTCSessionDescription({sdp: sdpAnswer, type: 'answer'}),
      );
    }
  };

  handleCandidate = async (candidate, sdpMid, sdpMLineIndex) => {
    if (this._connection) {
      const connection = await this.connection;
      connection.__addIceCandidate(
        new RTCIceCandidate({
          candidate,
          sdpMid,
          sdpMLineIndex,
        }),
      );
    }
  };
} 
